/*
 * THE CONVERSATIONS DATA LAYER — deliberately separate from anything that renders it.
 *
 * Every widget that shows the recents list is going to be redesigned, probably more than once. This
 * is the part that should NOT be: paging, the absolute count, prefetch-ahead, and the cache that
 * makes going back instant are behaviour, not presentation, and rewriting a list's markup should
 * never risk them.
 *
 * So the split is strict. This owns state and IO and knows nothing about the DOM — no Lit, no
 * elements, no styles. A view subscribes to `change`, reads the getters, and calls the verbs. Throw
 * the view away and the data layer is untouched; that is the point.
 *
 * WHAT IT GUARANTEES, each of which is a bug in the naive version:
 *
 *   • `total` is the ABSOLUTE count from the server, known after the first page. "30+" cannot answer
 *     "how many conversations do I have", which is the question the header is actually asked.
 *   • Pages PREFETCH ahead of the reader, so scrolling meets already-loaded rows instead of a gap.
 *   • The cache SURVIVES navigating into a conversation and back — the list is not re-fetched for a
 *     round trip the user made in two seconds.
 *   • One in-flight request at a time. Without that guard a fast scroll fires the same cursor
 *     repeatedly and appends the same rows.
 */
import { onConversationChange } from './conversation-events';
/**
 * Runway, as a fraction of one page.
 *
 * Half a page is enough to hide a fetch at human scroll speed — twenty-odd rows take far longer to
 * scroll past than a page takes to arrive. A FULL page of runway is subtly wrong: it can never be
 * satisfied by a freshly-loaded list, so opening the rail and touching one row immediately fetches a
 * second page, and most opens never read it (the rail's primary door is "new conversation", not the
 * list). Half spends nothing on those opens and still meets the scroller with loaded rows.
 */
const RUNWAY_FRACTION = 0.5;
/** Runway before the first page reveals the real page size. */
const RUNWAY_UNTIL_KNOWN = 10;
export class ConversationsStore extends EventTarget {
    constructor(loadPage) {
        super();
        this.loadPage = loadPage;
        this.items = [];
        this.cursor = '';
        this._total = -1;
        this._loading = false;
        this._failed = false;
        this.started = false;
        /** Set while a page is in flight, so concurrent asks share one request rather than racing. */
        this.inFlight = null;
        /** Derived from the first page rather than hardcoded, so this stays correct if the server changes
         *  how much it returns — a constant here would have to agree with a value it cannot see. */
        this.runway = RUNWAY_UNTIL_KNOWN;
        /** One page, with the single-flight guard. */
        /** Set by `refresh`: the next page fetched is the whole list again, not more of it. */
        this.replaceOnNextPage = false;
        // A conversation deleted ANYWHERE — this app's chat panel, the console's transcript browser,
        // another tab — is gone from this list too. Dropping it in place rather than refetching keeps
        // the pagination cursor and the loaded runway intact: a refetch here would reset the list to
        // page one and jump a reader who was scrolled halfway down it.
        this.stopListening = onConversationChange((change) => {
            if (change.kind !== 'deleted')
                return;
            const gone = new Set(change.ids);
            const before = this.items.length;
            this.items = this.items.filter((c) => !gone.has(c.id));
            if (this.items.length === before)
                return;
            // The absolute count is the server's, so it is adjusted rather than recomputed from what is
            // loaded — the header answers "how many do I have", not "how many have I scrolled past".
            if (this._total > 0)
                this._total = Math.max(0, this._total - (before - this.items.length));
            this.dispatchEvent(new CustomEvent('change'));
        });
    }
    /** Release the subscription. Call when the owner is finished with the store. */
    dispose() {
        this.stopListening();
    }
    /** The rows loaded so far, oldest-fetched first. Newest conversation first within that. */
    get conversations() {
        return this.items;
    }
    /** How many the user has IN TOTAL — not how many are loaded. -1 until the first page lands. */
    get total() {
        return this._total;
    }
    /** True while a page is being fetched. A view may show it; nothing depends on it. */
    get loading() {
        return this._loading;
    }
    /** False once every page has been read. */
    get hasMore() {
        return this.cursor !== '';
    }
    /**
     * The last page could not be fetched — the AI service did not answer.
     *
     * Kept apart from "loaded and empty" because the two call for opposite things: an empty list tells
     * a person they have no threads here, and saying that while the service is down tells them their
     * conversations are gone.
     */
    get failed() {
        return this._failed;
    }
    /** True before the first page resolves — distinct from "loaded and empty", which renders as a
     *  genuine empty state rather than a spinner. */
    get pending() {
        return !this.started;
    }
    /**
     * Load the first page, once.
     *
     * Idempotent by design: every view that mounts calls this, and a rail opened five times should not
     * fetch five times. Re-fetching is `refresh()`, which is a different intent and says so.
     */
    async start() {
        // A failed first page is not a loaded one: the next view that mounts asks again.
        if (this.started && !(this._failed && !this.items.length))
            return;
        this.started = true;
        await this.fetchNext();
    }
    /** Discard everything and load again — after a new conversation is created, say. */
    async refresh() {
        // The rows stay on screen while the fresh first page is fetched, and are REPLACED when it
        // lands. Emptying first painted a blank roster for the length of a round trip after every
        // delete — the person saw their conversations, then nothing, then their conversations again.
        this.cursor = '';
        this._total = -1;
        this.started = true;
        this.replaceOnNextPage = true;
        await this.fetchNext();
    }
    /**
     * Ensure `index` is loaded, with runway to spare.
     *
     * A view calls this with the last row it is about to render. Asking in terms of an INDEX rather
     * than a scroll position is what keeps the data layer free of the DOM: pixels, viewport heights
     * and scroll offsets are the view's business, and none of them belong in a cache.
     */
    async ensureLoadedThrough(index) {
        if (!this.hasMore)
            return;
        if (index + this.runway < this.items.length)
            return;
        await this.fetchNext();
    }
    /**
     * Warm the next page without being asked to show it.
     *
     * For hover: the moment a pointer lands on "back", the page the user is about to return to can
     * already be arriving. By the time the click resolves the rows are there and the return is
     * instant — the difference between a UI that responds and one that loads.
     *
     * Never throws and never blocks the caller. A speculative fetch that fails costs nothing, because
     * the real request will happen anyway.
     */
    prefetch() {
        if (!this.started) {
            void this.start().catch(() => undefined);
            return;
        }
        if (this.hasMore)
            void this.fetchNext().catch(() => undefined);
    }
    fetchNext() {
        if (this.inFlight)
            return this.inFlight;
        if (!this.replaceOnNextPage && this.started && !this.hasMore && this.items.length)
            return Promise.resolve();
        this._loading = true;
        this.emit();
        this.inFlight = (async () => {
            try {
                const page = await this.loadPage(this.cursor);
                this._failed = page.failed === true;
                // A failed page carries no cursor and no rows; applying it would end the list and, after a
                // refresh, wipe the rows already on screen.
                if (this._failed)
                    return;
                if (page.conversations.length) {
                    this.runway = Math.max(RUNWAY_UNTIL_KNOWN, Math.ceil(page.conversations.length * RUNWAY_FRACTION));
                }
                this.items = this.replaceOnNextPage ? page.conversations : [...this.items, ...page.conversations];
                this.replaceOnNextPage = false;
                this.cursor = page.nextCursor;
                // -1 means "unchanged" — the server only counts on the first page, because the answer does
                // not move mid-session and an aggregate per scroll is a cost with no reader.
                if (page.total >= 0)
                    this._total = page.total;
            }
            finally {
                this._loading = false;
                this.inFlight = null;
                this.emit();
            }
        })();
        return this.inFlight;
    }
    emit() {
        this.dispatchEvent(new CustomEvent('change'));
    }
}
//# sourceMappingURL=conversations-store.js.map